/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Popover,
	TextField,
} from '@mui/material';
import { GridLegacy } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import InfoRounded from '@mui/icons-material/InfoRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import { CreateCourseViewModel } from '../CreateCourseViewModel';
import { useViewModel } from '../../../../../hooks/useViewModel';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { EmojiData } from '../../../../../models/components/PickEmojiModel';
import CreateCourseDialogViewStyles from './CreateCourseDialogViewStyles';

const CreateCourseView = observer((): React.ReactElement => {
	const viewModel: CreateCourseViewModel = useViewModel(CreateCourseViewModel);
	const { classes } = CreateCourseDialogViewStyles();
	const translation = useIntl();

	return (
		<>
			<Button variant='contained' color='secondary' disableElevation onClick={() => viewModel.dialog.openDialog()}>
				<AddCircleIcon fontSize='small' />
			</Button>
			<Dialog
				open={viewModel.dialog.open}
				onClose={() => {
					viewModel.closeCreateDialog();
				}}
				aria-labelledby='create-course-title'
				aria-describedby='create-course-description'
				maxWidth={false}
				classes={{ paper: classes.paper }}>
				<DialogTitle id='create-course-title' className={classes.dialogTitle}>
					<FormattedMessage id='courses.create_course_dialog.title' />
					<CancelIcon
						fontSize={'small'}
						className={classes.closeButton}
						onClick={() => {
							viewModel.closeCreateDialog();
						}}
					/>
				</DialogTitle>
				<DialogContent>
					<GridLegacy container spacing={2}>
						<GridLegacy item xs={3}>
							<label id='identifier' className={classes.label}>
								<FormattedMessage id={'courses.create_course_dialog.identifier'} />
							</label>
							<Box onClick={viewModel.popover.openPopover} className={classes.emojiPicker}>
								<span style={{ fontSize: '22px' }}>{viewModel.getEmoji().native}</span>
							</Box>
							<Popover
								id={viewModel.popover.open ? 'pickEmoji popover' : undefined}
								open={viewModel.popover.open}
								anchorEl={viewModel.popover.anchor}
								onClose={viewModel.popover.closePopover}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}>
								{viewModel.popover.open && (
									<Picker
										data={data}
										set='native'
										onEmojiSelect={(emoji: EmojiData) => {
											viewModel.setEmoji(emoji);
											viewModel.popover.closePopover();
										}}
										previewEmoji='point_up'
										searchPlaceholder={translation.formatMessage({ id: 'courses.create_course_dialog.search_emoji' })}
									/>
								)}
							</Popover>
						</GridLegacy>
						<GridLegacy item xs={9}>
							<label id='courseName' className={classes.label}>
								<FormattedMessage id={'courses.create_course_dialog.course_name'} />
							</label>
							<TextField
								required
								name='courseName'
								variant='outlined'
								fullWidth={true}
								size={'small'}
								value={viewModel.getCourseName()}
								onChange={(event) => viewModel.setCourseName(event)}
							/>
						</GridLegacy>

						<GridLegacy item xs={12}>
							<label id='courseStartDate' className={classes.label}>
								<FormattedMessage id={'courses.create_course_dialog.course_start_date'} />
							</label>
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									slotProps={{
										textField: {
											size: 'small',
											variant: 'outlined',
											fullWidth: true,
											id: 'date-picker-inline',
										},
									}}
									format='dd.MM.yyyy'
									value={viewModel.getStartDate()}
									onChange={(newDate) => viewModel.setStartDate(newDate)}
								/>
							</LocalizationProvider>
						</GridLegacy>
					</GridLegacy>
				</DialogContent>
				<GridLegacy item xs={12} className={classes.infoIconBox}>
					<GridLegacy container direction='row' className={classes.infoBox}>
						<GridLegacy item xs={2} display={'flex'} justifyContent={'center'} alignItems={'center'}>
							<InfoRounded />
						</GridLegacy>
						<GridLegacy item xs={10}>
							<FormattedMessage id={'courses.create_course_dialog.course_successfully_created_hint'} />
						</GridLegacy>
					</GridLegacy>
				</GridLegacy>
				<DialogActions className={classes.dialogActions}>
					<Button
						disabled={viewModel.disableCreateButton()}
						onClick={() =>
							viewModel.createCourse(viewModel.getEmoji(), viewModel.getCourseName(), viewModel.getStartDate())
						}
						variant='contained'
						color='secondary'>
						<FormattedMessage id={'courses.create_course_dialog.create'} />
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
});

export default CreateCourseView;
